import React, { useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { Typography } from '@mui/material';
import ChipInput from '../ChipInput/ChipInput';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

/**
 * NodeChipInput — Chip-based input accepting text values and workflow placeholder tokens.
 * Wraps the existing ChipInput with node-appropriate styling and event isolation.
 *
 * Usage:
 *   <NodeChipInput
 *     label="Recipients"
 *     value={recipients}
 *     onChange={(newValues) => setRecipients(newValues)}
 *     availablePlaceholders={placeholders}
 *     placeholder="Type an email or $ for placeholders"
 *   />
 */

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: '4px',
  userSelect: 'none',
}));

const ChipInputWrapper = styled('div')(({ theme }) => ({
  width: '100%',
  // Override ChipInput's Wrapper to match node styling
  '& > .MuiFormControl-root > div': {
    borderRadius: NODE_UI.borderRadiusSm,
    fontSize: NODE_UI.fontSize.value,
    minHeight: '32px',
    borderColor: theme.palette.divider,
    backgroundColor: theme.palette.background.default,
    '&:hover': {
      borderColor: theme.palette.text.secondary,
    },
    '&.focused': {
      borderColor: theme.palette.text.primary,
      boxShadow: 'none',
    },
  },
  '& input': {
    fontSize: `${NODE_UI.fontSize.value} !important`,
  },
}));

const NodeChipInput = React.memo(function NodeChipInput({
  label,
  value = [],
  onChange,
  availablePlaceholders = [],
  placeholder,
  inputType,
  disabled = false,
  sx,
}) {
  const handlePlaceholderSelected = useCallback((selected) => {
    if (!selected) return;
    const chipValue = selected.isNotPlaceholder
      ? selected.key
      : '${' + selected.key + '}';
    const newValues = [...value, chipValue];
    onChange?.(newValues);
  }, [value, onChange]);

  const handleDelete = useCallback((index) => {
    const newValues = value.filter((_, i) => i !== index);
    onChange?.(newValues);
  }, [value, onChange]);

  return (
    <div
      onMouseDown={stopPropagation}
      onClick={stopPropagation}
      onPointerDown={stopPropagation}
    >
      {label && <Label>{label}</Label>}
      <ChipInputWrapper sx={sx}>
        <ChipInput
          inputValue={value}
          placeholders={availablePlaceholders}
          onPlaceholderSelected={handlePlaceholderSelected}
          onDelete={handleDelete}
          placeholder={placeholder}
          fullWidth
          inputType={inputType}
        />
      </ChipInputWrapper>
    </div>
  );
});

export default NodeChipInput;
